import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Divider, Form } from "semantic-ui-react";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import FormInput from "../components/FormInput";
import FormSelect from "../components/FormSelect";
import SaveButton from "../components/SaveButton";
import ConstantStore from "~/stores/ConstantStore";
import TourStore from "~/stores/TourStore";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import ModalError from "../../../components/element/ModalError";
import "../styles/TourInfo.less";
import TourOpenHouse from "../components/TourOpenHouse";
import FormEditor from "../components/FormEditor";
import ContentLoader from "~/components/element/ContentLoader";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import StickyMenu from "~/components/StickyMenu";
import ToastMessageStore from "~/stores/ToastMessageStore";

@observer
class Info extends Component {
  constructor(props) {
    super(props);
    document.title = "Tour - Info";
  }

  componentDidMount() {
    const { constantStore } = this.props;
    constantStore.load([ConstantStore.CONSTANTS.TOUR_PROPERTY_STATUS, ConstantStore.CONSTANTS.TOUR_PROPERTY_TYPE]);
    window.scrollTo(0, 0);
  }

  save() {
    const { t, tourStore, toastMessageStore } = this.props;

    tourStore.save().then(() => {
      toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
    });
  }

  render() {
    const { tourStore, constantStore, t, tourLayoutContentContainerRef } = this.props;
    const propertyStatus = constantStore.getFormList(ConstantStore.CONSTANTS.TOUR_PROPERTY_STATUS);
    const propertyType = constantStore.getFormList(ConstantStore.CONSTANTS.TOUR_PROPERTY_TYPE);
    const { tour } = tourStore;

    if (tourStore.isLoading || constantStore.isLoading) {
      return <ContentLoader />;
    }

    return (
      <>
        <ModalUnsavedConfirmation
          when={tourStore.hasUnsavedData}
          onSave={() => this.save()}
          onDiscard={() => tourStore.reset()}
        />
        <LoadingWrapper loading={tourStore.isSaving} message={t("saving")} />
        <ModalError nestStore={tourStore} />

        <StickyMenu
          contextRef={tourLayoutContentContainerRef}
          headerText={t("clientPanelInfo:information")}
          button={<SaveButton disabled={!tourStore.hasUnsavedData} onClick={() => this.save()} />}
        />

        <Form className="tour-info-outer-wrap">
          <h4>{t("clientPanelInfo:propertyDetailsStatus")}</h4>
          <div className="tour-info-container">
            <FormInput
              className="tour-info-input"
              label={t("clientPanelInfo:price")}
              name="tour.propertyPrice"
              dataStore={tourStore}
            />
            <FormInput
              className="tour-info-input"
              label={t("clientPanelInfo:numberBedrooms")}
              dataStore={tourStore}
              name="tour.propertyNumBedrooms"
            />
            <FormInput
              className="tour-info-input"
              label={t("clientPanelInfo:numberBathrooms")}
              dataStore={tourStore}
              name="tour.propertyNumBathrooms"
            />
            <FormInput
              className="tour-info-input"
              label={t("clientPanelInfo:floorArea")}
              placeholder={t("clientPanelInfo:sqft")}
              dataStore={tourStore}
              name="tour.propertySize"
            />
            <FormInput
              className="tour-info-input"
              label={t("clientPanelInfo:lotSize")}
              placeholder={t("clientPanelInfo:sqft")}
              dataStore={tourStore}
              name="tour.propertyLotSize"
            />
            <FormInput
              className="tour-info-input"
              label={t("clientPanelInfo:yearBuilt")}
              dataStore={tourStore}
              name="tour.propertyYearBuilt"
            />
            <FormSelect
              className="tour-info-input"
              label={t("clientPanelInfo:propertyType")}
              options={propertyType}
              placeholder={t("clientPanelInfo:selectType")}
              dataStore={tourStore}
              name="tour.propertyType"
            />
            <FormSelect
              className="tour-info-input"
              label={t("clientPanelInfo:propertyStatus")}
              options={propertyStatus}
              dataStore={tourStore}
              placeholder={t("clientPanelInfo:selectStatus")}
              name="tour.propertyStatus"
            />
          </div>
          <Divider className="tour-info-divider" />
          <TourOpenHouse tourStore={tourStore} />
          <Divider className="tour-info-divider" />

          <h4>{t("clientPanelInfo:headingsDescription")}</h4>
          <FormInput label={t("clientPanelInfo:heading", { number: 1 })} dataStore={tourStore} name="tour.heading1" />
          <FormInput label={t("clientPanelInfo:heading", { number: 2 })} dataStore={tourStore} name="tour.heading2" />

          {tour.supportsHeading3 && (
            <FormInput
              label={t("clientPanelInfo:heading", { number: 3 })}
              dataStore={tourStore}
              name="tour.heading3"
              placeholder={t("clientPanelInfo:heading3Support")}
            />
          )}
          <FormEditor
            label={t("clientPanelInfo:propertyDescription")}
            dataStore={tourStore}
            name="tour.listingInformation"
            config={{ placeholder: t("startWriting") }}
          />
        </Form>
      </>
    );
  }
}

Info.propTypes = {
  tourStore: PropTypes.instanceOf(TourStore),
  constantStore: PropTypes.instanceOf(ConstantStore),
  t: PropTypes.func.isRequired,
  tourLayoutContentContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(TourLayoutWrapper(Info, TourNav.INFO));
